import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, FileText, Loader2, Plus, X } from "lucide-react";

import { DefaultButton, PrimaryButton } from "@/components/fluent-button-compat";
import type { TenderDate, TenderDateConflict } from "../../TenderSearchProfileAPI";
import { describeDateTime } from "./tenderPanelText";

/**
 * The tender's milestones as a vertical timeline — Angebotsfrist, Bindefrist, Leistungsbeginn,
 * Vertragsende and whatever the detailed analysis added on top.
 *
 * Everything shown is computed server-side: the German label, the chronological order and the
 * urgency grade. This component picks colours and lays them out; it does not decide what is urgent,
 * because the board's deadline column already answers that and two answers would disagree.
 *
 * The grades come from the board's own thresholds (critical ≤ 7 days, soon ≤ 14), so a row whose
 * deadline column is red never shows a green dot next to the same date.
 *
 * A date without a time reads „ganztägig“ — the server stores it as UTC midnight, which would
 * otherwise show as 01:00. The Angebotsfrist stands out, and where the documents contradict it the
 * server sends a `dateConflicts` entry: shown as a hint under the published date, never as a second
 * deadline.
 */

/** The deadline the whole board is about — drawn larger than the rest. */
const HIGHLIGHT_KEY = "angebotsfrist";

const DOT: Record<TenderDate["level"], string> = {
  past: "bg-muted-foreground/40",
  critical: "bg-red-600",
  soon: "bg-amber-500",
  future: "bg-emerald-600",
  // No date at all — a statement, not a deadline. Deliberately colourless: an undated entry that
  // looked "green" would read as "plenty of time", which is not something we know.
  unknown: "bg-muted-foreground/30",
};

const TEXT: Record<TenderDate["level"], string> = {
  past: "text-muted-foreground",
  critical: "text-red-700",
  soon: "text-amber-700",
  future: "text-emerald-700",
  unknown: "text-muted-foreground",
};

const timeOf = (iso: string): string =>
  new Date(iso).toLocaleTimeString("de-DE", { timeZone: "Europe/Berlin", hour: "2-digit", minute: "2-digit" });

export function TenderDateTimeline({
  dates,
  conflicts = [],
  people = {},
  onAdd,
  onRemove,
  onReviewConflict,
  busy,
}: {
  dates: TenderDate[];
  /** Analysis dates that contradict a published one (`row.dateConflicts`). */
  conflicts?: TenderDateConflict[];
  /** E-mail → name, so a hand-entered date names a person rather than an address. */
  people?: Record<string, string>;
  /** Rejects with the server's message when the input cannot be read — shown next to the form. */
  onAdd?: (date: { label: string; at?: string; note?: string }) => Promise<unknown>;
  onRemove?: (key: string) => Promise<unknown>;
  /** „Prüfen“ on a conflict — opens the documents the contradiction comes from. */
  onReviewConflict?: () => void;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [at, setAt] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!onAdd) return;
    setSaving(true);
    setError(null);
    try {
      await onAdd({ label, at: at || undefined, note: note || undefined });
      setLabel("");
      setAt("");
      setNote("");
      setAdding(false);
    } catch (err: any) {
      // The server refuses an unreadable date rather than guessing — show exactly what it said.
      setError(err?.message ?? String(err));
    } finally {
      setSaving(false);
    }
  };

  const form = onAdd ? (
    <div>
      {adding ? (
        <div className="rounded-md border border-border p-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              autoFocus
              className="h-8 min-w-48 flex-1 rounded-md border border-input bg-transparent px-2 text-sm outline-none focus:border-ring"
              placeholder={t("tenderBoard.dates.labelPlaceholder")}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
            <input
              type="datetime-local"
              className="h-8 rounded-md border border-input bg-transparent px-2 text-sm outline-none focus:border-ring"
              value={at}
              onChange={(event) => setAt(event.target.value)}
            />
          </div>
          <input
            className="marginTopS h-8 w-full rounded-md border border-input bg-transparent px-2 text-sm outline-none focus:border-ring"
            placeholder={t("tenderBoard.dates.notePlaceholder")}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          {error && <div className="marginTopS text-xs text-red-700">{error}</div>}
          <div className="marginTopS flex items-center gap-2">
            <PrimaryButton disabled={saving || !label.trim()} onClick={submit}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {t("tenderBoard.dates.save")}
            </PrimaryButton>
            <DefaultButton
              disabled={saving}
              onClick={() => {
                setAdding(false);
                setError(null);
              }}
            >
              {t("global.cancel", { defaultValue: "Abbrechen" })}
            </DefaultButton>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setAdding(true)}
        >
          <Plus size={12} /> {t("tenderBoard.dates.add")}
        </button>
      )}
    </div>
  ) : null;

  const absolute = (iso: string) => {
    const { date, time } = describeDateTime(iso);
    return time ? `${date}, ${time} Uhr` : `${date} · ${t("tenderBoard.dates.allDay")}`;
  };
  const relative = (days: number) =>
    days < 0
      ? t("tenderBoard.dates.ago", { days: Math.abs(days) })
      : days === 0
        ? t("tenderBoard.dates.today")
        : days === 1
          ? t("tenderBoard.dates.tomorrow")
          : t("tenderBoard.dates.inDays", { days });
  const sourceChip = (date: TenderDate): string | null => {
    if (date.source === "derived") return t("tenderBoard.dates.derived");
    if (date.source === "manual") {
      const by = date.createdBy ?? null;
      return by ? t("tenderBoard.dates.manualBy", { name: people[by] ?? by.replace(/@.*$/, "") }) : t("tenderBoard.dates.manual");
    }
    return date.origin ?? null;
  };
  const conflictText = (conflict: TenderDateConflict) => {
    const say = (iso: string | null) => {
      if (!iso) return "–";
      const { date, time } = describeDateTime(iso);
      return time ? `${date}, ${timeOf(iso)} Uhr` : date;
    };
    const sameDay =
      !!conflict.notice.at &&
      !!conflict.other.at &&
      describeDateTime(conflict.notice.at).date === describeDateTime(conflict.other.at).date;
    // Only the time differs → name only the times, as people say it („14:00 Uhr, die Unterlagen 12:00“).
    const notice = sameDay && conflict.notice.at ? `${timeOf(conflict.notice.at)} Uhr` : say(conflict.notice.at);
    const other = sameDay && conflict.other.at ? timeOf(conflict.other.at) : say(conflict.other.at);
    return conflict.other.origin
      ? t("tenderBoard.dates.conflictWithSource", { notice, other, source: conflict.other.origin })
      : t("tenderBoard.dates.conflict", { notice, other });
  };

  return (
    <>
      {dates.length === 0 ? (
        <div className="text-xs text-muted-foreground">{t("tenderBoard.dates.empty")}</div>
      ) : (
        <ol className="relative ml-2 border-l border-border pl-6">
          {dates.map((date) => {
            const highlight = date.key === HIGHLIGHT_KEY;
            const chip = sourceChip(date);
            const own = conflicts.filter((conflict) => conflict.key === date.key);
            return (
              <li key={date.key} className="relative pb-4 last:pb-0" data-date-key={date.key}>
                <span
                  className={`absolute top-1 rounded-full ring-2 ring-background ${DOT[date.level]} ${
                    highlight ? "-left-[33px] h-4 w-4" : "-left-[31px] h-3 w-3"
                  }`}
                  aria-hidden
                />
                <div
                  className={`flex items-start gap-2 ${
                    highlight ? "-ml-2 rounded-md border border-border bg-muted/50 px-2 py-1.5" : ""
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className={`leading-snug ${highlight ? "text-base font-semibold" : "text-sm font-medium"}`}>
                      {date.label}
                    </div>
                    {date.at ? <div className="text-xs text-muted-foreground">{absolute(date.at)}</div> : null}
                    {/* The derivation, or the analysis' own wording when it names no date. Both are the
                        reason the entry says what it says, so neither is decoration. */}
                    {date.note ? <div className="text-xs text-muted-foreground">{date.note}</div> : null}
                    {date.daysUntil !== null && (
                      <div className={`text-xs font-medium ${TEXT[date.level]}`}>{relative(date.daysUntil)}</div>
                    )}
                  </div>
                  {/* Where it comes from. A computed date is marked as such — "published" and "we worked
                      it out" do not deserve the same confidence. */}
                  {chip ? (
                    <span
                      className="mt-0.5 flex shrink-0 items-center gap-1 rounded border border-border bg-background px-1.5 py-px text-[10px] text-muted-foreground"
                      title={date.source === "derived" ? t("tenderBoard.dates.derivedHint") : chip}
                    >
                      <FileText size={10} aria-hidden />
                      {chip}
                    </span>
                  ) : null}
                  {/* Only a hand-entered date can be removed — nothing published is deletable here. */}
                  {date.source === "manual" && onRemove ? (
                    <button
                      type="button"
                      disabled={busy}
                      title={t("tenderBoard.dates.remove")}
                      aria-label={t("tenderBoard.dates.remove")}
                      className="mt-0.5 shrink-0 text-muted-foreground hover:text-red-700"
                      onClick={() => onRemove(date.key)}
                    >
                      <X size={12} />
                    </button>
                  ) : null}
                </div>
                {own.map((conflict, index) => (
                  <div
                    key={index}
                    role="note"
                    className="mt-1.5 flex items-start gap-2 rounded border border-amber-400 bg-amber-50 px-2 py-1.5 text-xs text-amber-900"
                  >
                    <AlertTriangle size={12} className="mt-px shrink-0" aria-hidden />
                    <span className="flex-1">{conflictText(conflict)}</span>
                    {onReviewConflict && (
                      <button
                        type="button"
                        className="shrink-0 font-semibold underline underline-offset-2 hover:text-amber-700"
                        onClick={onReviewConflict}
                      >
                        {t("tenderBoard.dates.review")}
                      </button>
                    )}
                  </div>
                ))}
              </li>
            );
          })}
        </ol>
      )}
      <div className="mt-3">{form}</div>
    </>
  );
}

export default TenderDateTimeline;
